import { POProductConfirmation, TableColumn } from "src/types";
import { ProductColumnHeader } from "src/enums";
import { Product } from "src/types";
import { Table } from "src/components";
import { DD_MM_YYYY, hasAnyWarehouseSplit, splitByWarehouseRatio } from "src/helpers";
import { useAuthorisation } from "src/context";

interface ProductConfirmationTableProps {
  products: Product[];
  productConfirmations: POProductConfirmation[];
}

const ProductConfirmationTable: React.FC<ProductConfirmationTableProps> = ({ products, productConfirmations }) => {
  const { userRole } = useAuthorisation();
  const showWarehouseBreakdown = hasAnyWarehouseSplit(products) && userRole !== "VENDOR";

  const ProductConfirmationTableColumns: TableColumn<ProductColumnHeader, Product>[] = [
    {
      header: ProductColumnHeader.ID,
      column: (row) => <div className="text-sm text-gray-700">{row.id}</div>,
      styles: { minWidth: "80px" }
    },
    {
      header: ProductColumnHeader.NAME,
      column: (row) => <div className="text-sm text-gray-700">{row.name}</div>,
      styles: { minWidth: "200px" }
    },
    {
      header: ProductColumnHeader.SIZE,
      column: (row) => <div className="text-sm text-gray-700">{row.size}</div>,
      styles: { minWidth: "100px" }
    },
    {
      header: showWarehouseBreakdown ? (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs">Order Qty</span>
          <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
            <span className="text-violet-300">UK</span>
            <span className="text-orange-300">EU</span>
          </div>
        </div>
      ) : (
        ProductColumnHeader.ORDER_QUANTITY
      ),
      column: (row) =>
        showWarehouseBreakdown ? (
          <div className="flex flex-col items-center gap-0.5">
            <span className="font-semibold text-secondary-900 tabular-nums">{row.quantity}</span>
            <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
              <span className="text-right text-violet-600">{row.ukQuantity ?? 0}</span>
              <span className="text-left text-orange-600">{row.euQuantity ?? 0}</span>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-700 text-center">{row.quantity.toString()}</div>
        ),
      styles: { minWidth: showWarehouseBreakdown ? "120px" : "120px", textAlign: "center" }
    },
    {
      header: showWarehouseBreakdown ? (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs">Confirmed Qty</span>
          <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
            <span className="text-violet-300">UK</span>
            <span className="text-orange-300">EU</span>
          </div>
        </div>
      ) : (
        ProductColumnHeader.CONFIRMED_QTY
      ),
      column: (row) => {
        const confirmedQty = productConfirmations?.find(
          (productConfirmation) => productConfirmation.productId === row.id
        )?.confirmedQty;

        if (!showWarehouseBreakdown) {
          return <div className="text-sm text-gray-700 font-medium text-center">{confirmedQty?.toString() ?? ""}</div>;
        }

        const confirmedQtyClass =
          confirmedQty === undefined
            ? "text-secondary-500"
            : confirmedQty === row.quantity
              ? "text-success-700"
              : "text-error-700";

        const { uk: confirmedUk, eu: confirmedEu } = splitByWarehouseRatio(
          confirmedQty,
          row.ukQuantity || 0,
          row.euQuantity || 0
        );

        return (
          <div className="flex flex-col items-center gap-0.5">
            <span className={`font-semibold tabular-nums ${confirmedQtyClass}`}>
              {confirmedQty !== undefined ? confirmedQty : "-"}
            </span>
            {confirmedQty !== undefined && (
              <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
                <span className="text-right text-violet-600">{confirmedUk}</span>
                <span className="text-left text-orange-600">{confirmedEu}</span>
              </div>
            )}
          </div>
        );
      },
      styles: { minWidth: showWarehouseBreakdown ? "130px" : "120px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.QTY_DIFFERENCE,
      column: (row) => {
        const difference =
          Number(row.quantity) -
          Number(
            productConfirmations?.find((productConfirmation) => productConfirmation.productId === row.id)?.confirmedQty
          );
        const colorClass = difference > 0 ? "text-error-600" : difference < 0 ? "text-warning-600" : "text-success-600";
        return <div className={`text-sm font-semibold text-center ${colorClass}`}>{difference.toString()}</div>;
      },
      styles: { minWidth: "120px", textAlign: "center" }
    }
  ];

  // Add EST_RESTOCK_DATE column if any product has estRestockDate
  if (productConfirmations?.some((product) => product.estRestockDate || product.isDiscontinued)) {
    ProductConfirmationTableColumns.push({
      header: ProductColumnHeader.EST_RESTOCK_DATE,
      column: (row: Product) => {
        const product = productConfirmations?.find((product) => product.productId === row.id);
        const estRestockDate = product?.estRestockDate;
        const isDiscontinued = product?.isDiscontinued;
        if (isDiscontinued) {
          return <span className="text-secondary-500">Discontinued</span>;
        }
        return estRestockDate ? (
          <span className="text-secondary-700">{DD_MM_YYYY.formatDate(new Date(estRestockDate))}</span>
        ) : (
          <span className="text-secondary-500">-</span>
        );
      },
      styles: { minWidth: "150px" }
    });
  }

  return <Table<ProductColumnHeader, Product> data={products} columns={ProductConfirmationTableColumns} />;
};

export { ProductConfirmationTable };
